import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import MaterialIcon from "./MaterialIcon";
import { callAPI } from "../helper/apiUtils";
import EmailClassifyBusyOverlay from "./EmailClassifyBusyOverlay";
import { classifyProgressLabel } from "../utils/emailClassifyWithProgress";
import "./NewRoomModal.css";

function isKjuupCompany(c) {
  return !!(c && (c.isKjuup === true || c.isKjuup === 1 || c.isKjuup === "1"));
}

/**
 * NewRoomModal
 *
 * Shown from the email-assign flow when the user typed a project/room name that
 * does NOT match an existing room (i.e. they are about to create a brand-new
 * room). Confirms the room name, asks External vs Internal, and — for internal
 * rooms with more than one Kjuup+ company — which company the room belongs to.
 * External rooms still offer a Firmenverbindung when the user has several
 * companies. Membership is re-verified server-side.
 *
 * Reuses the shared .email-classify-* modal styles.
 */
function NewRoomModal({ initialName, onCreate, onClose, busy, progress, strings = {}, submitLabel, busyLabel }) {
  const [name, setName] = useState(initialName || "");
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roomType, setRoomType] = useState("external"); // "external" | "internal"
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  const kjuupCompanies = useMemo(() => companies.filter(isKjuupCompany), [companies]);
  const isInternal = roomType === "internal";
  const canCreateInternal = kjuupCompanies.length > 0;
  const typeCompanies = isInternal ? kjuupCompanies : companies;
  const needsCompanyPicker = typeCompanies.length > 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await callAPI(`/company/my`, {}, "GET");
        const data = res?.data || res;
        const list = data && Array.isArray(data.companies) ? data.companies : [];
        if (!cancelled) {
          setCompanies(list);
          if (list.length >= 1) setCompanyId(String(list[0].id));
        }
      } catch (e) {
        if (!cancelled) setCompanies([]);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectType = (next) => {
    if (next === "internal" && !canCreateInternal) return;
    setRoomType(next);
    setShowInfo(false);
    const list = next === "internal" ? kjuupCompanies : companies;
    setCompanyId(list.length >= 1 ? String(list[0].id) : "");
  };

  const create = () => {
    const clean = name.trim();
    if (!clean || busy || loadingCompanies) return;
    if (isInternal && !canCreateInternal) return;
    if (needsCompanyPicker && !companyId) return;
    const resolvedCompanyId = isInternal
      ? String(
          (kjuupCompanies.find((c) => String(c.id) === String(companyId)) || kjuupCompanies[0] || {}).id || ""
        ) || null
      : (typeCompanies.length >= 1 ? (companyId || String(typeCompanies[0].id)) : null);
    if (isInternal && !resolvedCompanyId) return;
    onCreate(clean, resolvedCompanyId, isInternal);
  };

  return ReactDOM.createPortal(
    <div
      className="email-classify-overlay email-classify-overlay--portal"
      role="dialog"
      aria-modal="true"
      onClick={busy ? undefined : onClose}
    >
      <div className={`email-classify-modal${busy ? " email-classify-modal--busy" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="email-classify-header">
          <div className="email-classify-title">
            <span>{strings.email_new_room_title || "Create new project"}</span>
          </div>
          <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={onClose} disabled={busy} title={strings.cancel || "Close"}>
            <MaterialIcon icon="close" size={18} />
          </button>
        </div>

        <div className="email-classify-section">
          <div className="email-classify-question">
            {strings.email_new_room_name || "Project name"}
          </div>
          <input
            className="email-classify-input"
            type="text"
            value={name}
            placeholder={strings.email_project_name || "Project name"}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                create();
              }
            }}
            disabled={busy}
            autoFocus
            autoComplete="off"
          />

          <div className="email-classify-question new-room-modal__question">
            {strings.email_new_room_type || "Project type"}
          </div>
          <div className="email-classify-type-row">
            <button
              type="button"
              className={`email-classify-type-btn email-classify-type-btn--external${roomType === "external" ? " is-selected" : ""}`}
              onClick={() => selectType("external")}
              disabled={busy}
            >
              {strings.email_new_room_external || strings.external_projects || "External"}
            </button>
            <button
              type="button"
              className={`email-classify-type-btn email-classify-type-btn--internal${roomType === "internal" ? " is-selected" : ""}`}
              onClick={() => selectType("internal")}
              disabled={busy || !canCreateInternal}
              title={
                !canCreateInternal
                  ? (strings.email_new_room_internal_unavailable || "Join a Kjuup+ company to create internal projects")
                  : undefined
              }
            >
              {strings.email_new_room_internal || strings.internal_project || "Internal"}
            </button>
          </div>

          {needsCompanyPicker && (
            <>
              <div
                className="email-classify-question new-room-modal__question new-room-modal__question--row"
              >
                <span>
                  {isInternal
                    ? (strings.email_new_room_kjuup_company || "Select Kjuup+ company")
                    : (strings.email_new_room_company || "Select company connection")}
                </span>
                <button
                  type="button"
                  onClick={() => setShowInfo((v) => !v)}
                  title={strings.info || "Info"}
                  aria-expanded={showInfo}
                  className="kj-btn kj-btn--icon kj-btn--sm kj-btn--email"
                >
                  <MaterialIcon icon="info" size={16} color="#e066ed" />
                </button>
              </div>
              {showInfo && (
                <div className="new-room-modal__info">
                  {isInternal
                    ? (strings.email_new_room_kjuup_company_info ||
                      "The internal project will belong to this Kjuup+ company.")
                    : (strings.email_new_room_company_info ||
                      "Choose the company connection you want to be listed with in the project. This is shown to other members.")}
                </div>
              )}
              <select
                className="email-classify-input"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={busy}
              >
                {typeCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.companyName || ""}
                  </option>
                ))}
              </select>
            </>
          )}

          <button
            className="kj-btn kj-btn--primary kj-btn--email kj-btn--block email-classify-btn new-room-modal__create-btn"
            onClick={create}
            disabled={busy || !name.trim() || (needsCompanyPicker && !companyId) || (isInternal && !canCreateInternal)}
          >
            {busy ? (
              <span className="email-classify-btn-spinner" aria-hidden="true" />
            ) : (
              <MaterialIcon icon="add" size={16} />
            )}
            <span>
              {busy
                ? (strings.email_associating_to_project || "Saving email to project...")
                : (submitLabel || strings.email_new_room_create || "Create project & save email")}
            </span>
          </button>
        </div>

        {busy && (
          <EmailClassifyBusyOverlay
            label={busyLabel || classifyProgressLabel(progress || { phase: "saving" }, strings)}
            completed={progress && progress.completed}
            total={progress && progress.total}
          />
        )}
      </div>
    </div>,
    document.body
  );
}

export default NewRoomModal;
